import { inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';

import { DepositBloc } from '@connect-services/client/blocs/deposit.bloc';
import { DepositFolderListBloc } from '@connect-services/client/blocs/deposit-folder-list.bloc';
import { DepositListWithFolderAndStandardBloc } from '@connect-services/client/blocs/deposit-list-with-folder-and-standard.bloc';
import { DepositWithFolderAndStandardBloc } from '@connect-services/client/blocs/deposit-with-folder-and-standard.bloc';
import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionEntitiesListBloc } from '@connect-services/client/blocs/field-intervention-entities-list.bloc';
import { FieldInterventionEntityBloc } from '@connect-services/client/blocs/field-intervention-entity.bloc';
import { FieldInterventionFormBloc } from '@connect-services/client/blocs/field-intervention-form-bloc';
import { FieldInterventionFormListBloc } from '@connect-services/client/blocs/field-intervention-form-list.bloc';
import { FieldInterventionLayerBloc } from '@connect-services/client/blocs/field-intervention-layer.bloc';
import { FieldInterventionLayerListBloc } from '@connect-services/client/blocs/field-intervention-layer-list.bloc';
import { FieldInterventionListBloc } from '@connect-services/client/blocs/field-intervention-list.bloc';
import { LayerBloc } from '@connect-services/client/blocs/layer.bloc';
import { LayerEntityBloc } from '@connect-services/client/blocs/layer-entity.bloc';
import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { MapFilterBloc } from '@connect-services/client/blocs/map-filter.bloc';
import { PeopleBloc } from '@connect-services/client/blocs/people.bloc';
import { PeopleListBloc } from '@connect-services/client/blocs/people-list.bloc';
import { PermissionListBloc } from '@connect-services/client/blocs/permission-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { RoleBloc } from '@connect-services/client/blocs/role.bloc';
import { RoleListBloc } from '@connect-services/client/blocs/role-list.bloc';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { DataViewType } from '@connect-services/client/pages/management/data/deposit/deposit.component';
import { EditAction, Layer } from '@connect-services/client/sdk/api';
import { DetailPanelService } from '@connect-services/client/services/detail-panel.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { defaultFieldInterventionLayerListPaginationParameters } from '@connect-services/client/services/entities-table-by-layer.service';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { WarehouseAndFormsAccessPermissionsService } from '@connect-services/client/services/warehouse-and-forms-access-permissions.service';
import { defaultPaginationParameters, OrderEnum, paginationMaxTake } from '@shared/client/models/pagination.model';

type ResolverDeps = {
    organizationAccessPermissionsService: OrganizationAccessPermissionsService;
    warehouseAndFormsAccessPermissionsService: WarehouseAndFormsAccessPermissionsService;
};

type Injectors = {
    depositBloc: DepositBloc;
    depositFolderListBloc: DepositFolderListBloc;
    depositListWithFolderAndStandardBloc: DepositListWithFolderAndStandardBloc;
    depositWithFolderAndStandardBloc: DepositWithFolderAndStandardBloc;
    detailPanelService: DetailPanelService;
    editModeStateService: EditModeStateService;
    fieldInterventionBloc: FieldInterventionBloc;
    fieldInterventionEntitiesListBloc: FieldInterventionEntitiesListBloc;
    fieldInterventionEntityBloc: FieldInterventionEntityBloc;
    fieldInterventionFormBloc: FieldInterventionFormBloc;
    fieldInterventionFormListBloc: FieldInterventionFormListBloc;
    fieldInterventionLayerBloc: FieldInterventionLayerBloc;
    fieldInterventionLayerListBloc: FieldInterventionLayerListBloc;
    fieldInterventionListBloc: FieldInterventionListBloc;
    injector: Injector;
    layerBloc: LayerBloc;
    layerEntityBloc: LayerEntityBloc;
    layerListBloc: LayerListBloc;
    mapFilterBloc: MapFilterBloc;
    peopleBloc: PeopleBloc;
    peopleListBloc: PeopleListBloc;
    permissionListBloc: PermissionListBloc;
    projectBloc: ProjectBloc;
    roleBloc: RoleBloc;
    roleListBloc: RoleListBloc;
};

export async function blocResolver(route: ActivatedRouteSnapshot): Promise<void> {
    // Injections must be declared here to avoid Angular error: NG0203 about injection context
    const injectors: Injectors = {
        depositBloc: inject(DepositBloc),
        depositFolderListBloc: inject(DepositFolderListBloc),
        depositListWithFolderAndStandardBloc: inject(DepositListWithFolderAndStandardBloc),
        depositWithFolderAndStandardBloc: inject(DepositWithFolderAndStandardBloc),
        detailPanelService: inject(DetailPanelService),
        editModeStateService: inject(EditModeStateService),
        fieldInterventionBloc: inject(FieldInterventionBloc),
        fieldInterventionEntitiesListBloc: inject(FieldInterventionEntitiesListBloc),
        fieldInterventionEntityBloc: inject(FieldInterventionEntityBloc),
        fieldInterventionFormBloc: inject(FieldInterventionFormBloc),
        fieldInterventionFormListBloc: inject(FieldInterventionFormListBloc),
        fieldInterventionLayerBloc: inject(FieldInterventionLayerBloc),
        fieldInterventionLayerListBloc: inject(FieldInterventionLayerListBloc),
        fieldInterventionListBloc: inject(FieldInterventionListBloc),
        injector: inject(Injector),
        layerBloc: inject(LayerBloc),
        layerEntityBloc: inject(LayerEntityBloc),
        layerListBloc: inject(LayerListBloc),
        mapFilterBloc: inject(MapFilterBloc),
        peopleBloc: inject(PeopleBloc),
        peopleListBloc: inject(PeopleListBloc),
        permissionListBloc: inject(PermissionListBloc),
        projectBloc: inject(ProjectBloc),
        roleBloc: inject(RoleBloc),
        roleListBloc: inject(RoleListBloc),
    };

    const organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);
    const warehouseAndFormsAccessPermissionsService = inject(WarehouseAndFormsAccessPermissionsService);

    await inspectRoute(route, injectors, {
        organizationAccessPermissionsService,
        warehouseAndFormsAccessPermissionsService,
    });
}

function getRouteParam(route: ActivatedRouteSnapshot, routePath: RoutePathEnum): string {
    const params = route.paramMap.get(routePath);
    if (!params) {
        throw new Error('Missing route parameter');
    }

    return params;
}

async function inspectRoute(route: ActivatedRouteSnapshot, injectors: Injectors, deps: ResolverDeps): Promise<void> {
    const label: RoutePathEnum = (route.routeConfig?.path ?? '') as RoutePathEnum;

    await loadBloc(label, route, injectors, deps);

    if (!route.firstChild) {
        return;
    }
    await inspectRoute(route.firstChild, injectors, deps);
}

async function loadLayerEntityDetail(route: ActivatedRouteSnapshot, injectors: Injectors): Promise<void> {
    let layerId: string = '';

    if (route.queryParamMap.get('layer') !== null) {
        await injectors.layerBloc.load({ id: route.queryParamMap.get('layer')! }, { refresh: true });

        layerId = injectors.layerBloc.value()!.id;
    } else if (route.queryParamMap.get('name') !== null && route.queryParamMap.get('deposit') !== null) {
        await injectors.layerListBloc.load(
            {
                deposit: route.queryParamMap.get('deposit')!,
                project: route.paramMap.get('projectId') ?? undefined,
                searchValue: route.queryParamMap.get('name')!,
            },
            { refresh: true },
        );
        layerId =
            injectors.layerListBloc
                .value()!
                .items.find((layer: Layer) => layer.name === route.queryParamMap.get('name')!)?.id ?? '';

        await injectors.layerBloc.load({ id: layerId }, { refresh: true });
    }

    if (!layerId) {
        return;
    }

    await injectors.layerEntityBloc.load({
        entityId: getRouteParam(route, RoutePathEnum.TABLE_ENTITY_ID),
        id: layerId,
    });
}

async function waitServiceInitialized(
    organizationAccessPermissionsService: OrganizationAccessPermissionsService,
    injectors: Injectors,
): Promise<void> {
    await firstValueFrom(
        toObservable(organizationAccessPermissionsService.initialized, { injector: injectors.injector }).pipe(
            filter(Boolean),
        ),
    );
}

async function loadBloc(
    routeLabel: RoutePathEnum,
    route: ActivatedRouteSnapshot,
    injectors: Injectors,
    deps: ResolverDeps,
): Promise<void> {
    const splitRouteLabel = routeLabel.split(':').at(-1);
    if (!splitRouteLabel) {
        return;
    }

    const { organizationAccessPermissionsService, warehouseAndFormsAccessPermissionsService } = deps;

    switch (splitRouteLabel) {
        case RoutePathEnum.PROJECT_ID:
            await injectors.projectBloc.load({ id: getRouteParam(route, RoutePathEnum.PROJECT_ID) });
            await injectors.layerListBloc.load({
                project: getRouteParam(route, RoutePathEnum.PROJECT_ID),
                ...defaultPaginationParameters,
                take: paginationMaxTake,
            });
            break;

        case RoutePathEnum.ORGANIZATION_DATA:
            if (!warehouseAndFormsAccessPermissionsService.canAccessWarehouse()) {
                break;
            }

            await injectors.depositFolderListBloc.load(
                {
                    ...defaultPaginationParameters,
                    isCaseSensitive: true,
                    order: OrderEnum.ASC,
                    orderBy: 'name',
                    take: 10,
                    type: DataViewType.ORGANIZATION_DATA,
                },
                { refresh: true },
            );
            break;

        case RoutePathEnum.REFERENCE_DATA:
            if (!warehouseAndFormsAccessPermissionsService.canAccessWarehouse()) {
                break;
            }

            await injectors.depositFolderListBloc.load(
                {
                    ...defaultPaginationParameters,
                    isCaseSensitive: true,
                    order: OrderEnum.ASC,
                    orderBy: 'name',
                    take: 10,
                    type: DataViewType.REFERENCE_DATA,
                },
                { refresh: true },
            );
            break;

        case RoutePathEnum.DEPOSIT_ID:
            await injectors.depositBloc.load({
                id: getRouteParam(route, RoutePathEnum.DEPOSIT_ID),
            });
            break;

        case RoutePathEnum.ROLES:
            await waitServiceInitialized(organizationAccessPermissionsService, injectors);
            if (!organizationAccessPermissionsService.canAccessRole()) {
                break;
            }

            await injectors.roleListBloc.load({ ...defaultPaginationParameters }, { refresh: true });
            break;

        case RoutePathEnum.NOTIFICATION:
            await injectors.fieldInterventionListBloc.load(
                {
                    ...defaultPaginationParameters,
                    order: OrderEnum.ASC,
                    orderBy: 'name',
                    take: 9,
                },
                { refresh: true },
            );
            await injectors.peopleListBloc.load(
                {
                    ...defaultPaginationParameters,
                    order: OrderEnum.ASC,
                    orderBy: 'givenName',
                    take: 9,
                },
                { refresh: true },
            );
            break;
        case RoutePathEnum.USERS:
            await waitServiceInitialized(organizationAccessPermissionsService, injectors);
            if (!organizationAccessPermissionsService.canAccessUser()) {
                break;
            }

            await injectors.peopleListBloc.load(
                {
                    ...defaultPaginationParameters,
                    order: OrderEnum.ASC,
                    orderBy: 'familyName',
                },
                { refresh: true },
            );

            break;

        case RoutePathEnum.USERS_ROLES:
            await waitServiceInitialized(organizationAccessPermissionsService, injectors);
            if (organizationAccessPermissionsService.canAccessRole()) {
                await injectors.roleListBloc.load({
                    ...defaultPaginationParameters,
                    take: 4,
                });
            }

            if (organizationAccessPermissionsService.canAccessUser()) {
                await injectors.peopleListBloc.load(
                    {
                        ...defaultPaginationParameters,
                        order: OrderEnum.ASC,
                        orderBy: 'familyName',
                        take: 4,
                    },
                    { refresh: true },
                );
            }
            break;
        case RoutePathEnum.USER_EDITION:
        case RoutePathEnum.USER_ID:
            await waitServiceInitialized(organizationAccessPermissionsService, injectors);
            if (!organizationAccessPermissionsService.canAccessUser()) {
                break;
            }

            await injectors.peopleBloc.load({
                id: getRouteParam(route, RoutePathEnum.USER_ID),
            });
            break;
        case RoutePathEnum.ROLE_EDITION:
        case RoutePathEnum.ROLE_ID:
            await waitServiceInitialized(organizationAccessPermissionsService, injectors);
            if (!organizationAccessPermissionsService.canAccessRole()) {
                break;
            }

            await injectors.roleBloc.load(
                {
                    id: getRouteParam(route, RoutePathEnum.ROLE_ID),
                },
                { refresh: true },
            );

            await injectors.permissionListBloc.load({
                role: getRouteParam(route, RoutePathEnum.ROLE_ID),
                take: paginationMaxTake,
            });

            break;

        case RoutePathEnum.FIELD_INTERVENTION_CREATE:
            await injectors.fieldInterventionBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
            });
            break;
        case RoutePathEnum.ENTITY_ID:
            const layerId: string = getRouteParam(route, RoutePathEnum.LAYER_ID);
            const entityId: string = getRouteParam(route, RoutePathEnum.ENTITY_ID);

            await injectors.layerBloc.load({
                id: layerId,
            });

            if (route.firstChild?.routeConfig?.path === EditAction.CREATE) {
                break;
            }

            const isCreationInProgress: boolean = injectors.editModeStateService.isCreatedEntity(layerId, entityId);
            if (isCreationInProgress) {
                break;
            }

            await injectors.layerEntityBloc.load(
                {
                    entityId,
                    id: layerId,
                },
                { refresh: true },
            );
            break;
        case RoutePathEnum.FIELD_INTERVENTION_ID:
            await injectors.fieldInterventionBloc.load(
                {
                    id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
                },
                { refresh: true },
            );
            break;

        case RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID:
            await injectors.fieldInterventionEntityBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID),
                onlyBookmarked: true,
            });
            break;

        case RoutePathEnum.FIELD_INTERVENTIONS:
            await injectors.fieldInterventionListBloc.load({
                project: getRouteParam(route, RoutePathEnum.PROJECT_ID),
                ...defaultPaginationParameters,
                take: paginationMaxTake,
            });
            break;

        case RoutePathEnum.FIELD_INTERVENTION_DASHBOARD:
            await injectors.fieldInterventionLayerListBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
                ...defaultFieldInterventionLayerListPaginationParameters,
            });
            break;

        case RoutePathEnum.FIELD_INTERVENTION_PLANNING:
            await injectors.fieldInterventionLayerListBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
                ...defaultFieldInterventionLayerListPaginationParameters,
            });
            if (injectors.fieldInterventionEntitiesListBloc.value()) {
                break;
            }
            await injectors.fieldInterventionEntitiesListBloc.load(
                {
                    fieldIntervention: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
                    layers: injectors.fieldInterventionLayerListBloc.value()?.items?.[0]?.id
                        ? [injectors.fieldInterventionLayerListBloc.value()!.items[0]!.id]
                        : undefined,
                    ...defaultPaginationParameters,
                },
                { refresh: true },
            );
            break;

        case RoutePathEnum.FIELD_INTERVENTION_ENTITIES:
            await injectors.fieldInterventionBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
            });

            await injectors.fieldInterventionLayerListBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
                ...defaultFieldInterventionLayerListPaginationParameters,
            });

            if (injectors.fieldInterventionEntitiesListBloc.value()) {
                break;
            }
            await injectors.fieldInterventionEntitiesListBloc.load(
                {
                    fieldIntervention: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
                    layers: injectors.fieldInterventionLayerListBloc.value()?.items?.[0]?.id
                        ? [injectors.fieldInterventionLayerListBloc.value()!.items[0].id]
                        : undefined,
                    ...defaultPaginationParameters,
                },
                { refresh: true },
            );

            break;
        case RoutePathEnum.FORMS:
        case RoutePathEnum.FIELD_INTERVENTION_FORMS:
            await injectors.fieldInterventionFormListBloc.load(
                { ...defaultPaginationParameters, order: OrderEnum.ASC, orderBy: 'name' },
                { refresh: true },
            );
            break;

        case RoutePathEnum.FIELD_INTERVENTION_FORM_ID:
            await injectors.fieldInterventionFormBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_FORM_ID),
            });
            break;

        case RoutePathEnum.FIELD_INTERVENTION_LAYERS:
            await injectors.fieldInterventionBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
            });
            await injectors.fieldInterventionLayerListBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
                ...defaultFieldInterventionLayerListPaginationParameters,
            });
            break;
        case RoutePathEnum.FIELD_INTERVENTION_LAYER_ID:
            await injectors.fieldInterventionLayerBloc.load({
                id: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_ID),
                layer: getRouteParam(route, RoutePathEnum.FIELD_INTERVENTION_LAYER_ID),
                take: 1,
            });
            break;
        case RoutePathEnum.LAYER_FILTER:
            await injectors.depositFolderListBloc.load({
                ...defaultPaginationParameters,
                isCaseSensitive: true,
                order: OrderEnum.ASC,
                orderBy: 'name',
                project: getRouteParam(route, RoutePathEnum.PROJECT_ID),
                take: 8,
            });
            break;
        case RoutePathEnum.LAYER_FILTER_ID:
            await injectors.mapFilterBloc.load({
                id: getRouteParam(route, RoutePathEnum.LAYER_FILTER_ID),
            });
            await injectors.depositFolderListBloc.load({
                ...defaultPaginationParameters,
                isCaseSensitive: true,
                order: OrderEnum.ASC,
                orderBy: 'name',
                project: getRouteParam(route, RoutePathEnum.PROJECT_ID),
                take: 8,
            });
            break;
        case RoutePathEnum.SYNOPTIC:
            await injectors.projectBloc.load({ id: getRouteParam(route, RoutePathEnum.PROJECT_ID) });
            break;

        case RoutePathEnum.DEPOSIT_DETAIL:
            await injectors.roleListBloc.load({
                ...defaultPaginationParameters,
                take: 50,
            });
            break;

        case RoutePathEnum.DETAIL_LAYER_ID:
            await injectors.layerBloc.load(
                {
                    id: getRouteParam(route, RoutePathEnum.DETAIL_LAYER_ID),
                },
                { refresh: true },
            );

            if (injectors.layerBloc.value()) {
                injectors.detailPanelService.activeLayerType.set(injectors.layerBloc.value()!.type);
            }

            break;

        case RoutePathEnum.DEPOSIT_DETAIL_ID:
            await injectors.depositWithFolderAndStandardBloc.load(
                {
                    id: getRouteParam(route, RoutePathEnum.DEPOSIT_DETAIL_ID),
                },
                { refresh: true },
            );
            await injectors.layerListBloc.load(
                {
                    deposit: getRouteParam(route, RoutePathEnum.DEPOSIT_DETAIL_ID),
                    geomFilter: 'ALL',
                    orderBy: 'name',
                    project: getRouteParam(route, RoutePathEnum.PROJECT_ID),
                    take: 20,
                },
                { refresh: true },
            );
            break;

        case RoutePathEnum.TABLE_ENTITY_ID:
            await loadLayerEntityDetail(route, injectors);
    }
}
